import { useEffect, useState } from 'react'
import { X, RefreshCw } from 'lucide-react'
import { useManagers } from '../lib/useManagers'
import {
  useChangeAssignment,
  type Assignment,
  type ChangeAssignmentInput
} from '../lib/useAssignments'

interface Props {
  liverId: string
  liverName: string
  currentAssignment: Assignment | null
  open: boolean
  initialReason?: string | null
  initialNotes?: string | null
  onClose: () => void
}

const CHANGE_REASONS = [
  { value: '異動', label: '異動 (組織変更)' },
  { value: '退職', label: '退職 (前担当者の退職)' },
  { value: '担当再編', label: '担当再編 (再アサイン)' },
  { value: '初回アサイン', label: '初回アサイン' },
  { value: 'その他', label: 'その他' }
]

export function AssignmentChangeModal({
  liverId,
  liverName,
  currentAssignment,
  open,
  initialReason,
  initialNotes,
  onClose
}: Props) {
  const managers = useManagers()
  const change = useChangeAssignment()

  const [newManagerId, setNewManagerId] = useState('')
  const [reason, setReason] = useState<string>(
    currentAssignment ? '異動' : '初回アサイン'
  )
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset form whenever the modal is reopened.
  useEffect(() => {
    if (open) {
      setNewManagerId('')
      setReason(initialReason || (currentAssignment ? '異動' : '初回アサイン'))
      setNotes(initialNotes ?? '')
      setError(null)
    }
  }, [open, currentAssignment, initialReason, initialNotes])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!newManagerId) {
      setError('新しい担当者を選択してください')
      return
    }
    const input: ChangeAssignmentInput = {
      liverId,
      newManagerId,
      changeReason: reason || null,
      handoffNotes: notes.trim() || null
    }
    try {
      await change.mutateAsync(input)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  // Candidate managers exclude the current one (you can't reassign to the same person).
  const candidates = (managers.data ?? []).filter(
    (m) => m.id !== currentAssignment?.manager_id
  )

  return (
    <div
      className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {currentAssignment ? '担当マネージャーを変更' : '初回担当をアサイン'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              ライバー: <span className="font-medium">{liverName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
            title="閉じる (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {currentAssignment && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              現担当: <span className="font-medium text-gray-800">
                {currentAssignment.manager?.name ??
                  currentAssignment.manager?.email ??
                  '(不明)'}
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              新しい担当者 <span className="text-red-500">*</span>
            </label>
            {managers.isLoading ? (
              <div className="text-sm text-gray-400 py-2">読み込み中…</div>
            ) : (
              <select
                value={newManagerId}
                onChange={(e) => setNewManagerId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-sky-400 bg-white"
              >
                <option value="">— 担当者を選択 —</option>
                {candidates.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name ? `${m.name} (${m.email})` : m.email}
                    {m.department ? ` / ${m.department}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">変更理由</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-sky-400 bg-white"
            >
              {CHANGE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              引継ぎノート
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="ライバーの近況、性格、配信ジャンル、注意点など。後任の参考になる情報をここに残します。"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-sky-400 resize-y leading-relaxed"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              ヒント: 通話履歴・LINE・分析の自動生成サマリは将来このノートに自動で挿入できる予定です。
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={change.isPending || !newManagerId}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${change.isPending ? 'animate-spin' : ''}`} />
              {change.isPending
                ? '実行中…'
                : currentAssignment
                  ? '担当を切り替える'
                  : 'アサインする'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
